import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Table } from '@tanstack/react-table';
import { Pencil, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { BulkActionMenu } from './BulkActionMenu';
import type { RowAction } from './types';

interface StickyBulkActionBarProps<TData> {
  table: Table<TData>;
  renderBulkEdit?: (table: Table<TData>, close: () => void) => React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Row actions — used to surface bulk-capable actions (those with a `bulk`
   * descriptor) via the ellipsis dropdown.
   */
  actions?: RowAction<TData>[] | ((data: TData) => RowAction<TData>[]);
}

/**
 * Sticky action bar that appears at the bottom of its scroll container when table rows are selected.
 * Uses sticky positioning to stay within its panel, supporting multi-panel layouts.
 * Provides quick access to bulk actions with a dedicated bulk edit dialog.
 */
export function StickyBulkActionBar<TData>({
  table,
  renderBulkEdit,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  actions,
}: StickyBulkActionBarProps<TData>) {
  const [internalOpen, setInternalOpen] = useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen =
    controlledOnOpenChange !== undefined
      ? controlledOnOpenChange
      : setInternalOpen;

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedRowCount = selectedRows.length;
  const selectedData = useMemo(
    () => selectedRows.map((r) => r.original),
    [selectedRows],
  );

  const handleClearSelection = () => {
    table.setRowSelection({});
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (selectedRowCount === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        // Sticky positioning at bottom of scroll container (panel)
        'sticky bottom-0 z-10',
        // Centering container
        'flex justify-center',
        // Padding from edges
        'px-4 pb-4',
      )}
    >
      <div
        className={cn(
          // Styling
          `bg-background/95 border-border flex items-center gap-3 rounded-lg border px-4
          py-3 shadow-lg backdrop-blur-sm`,
          // Max width for larger screens
          'w-full max-w-md',
          // Left margin on mobile to avoid nav toggle
          'pl-16 md:ml-0',
        )}
      >
        {/* Selection count */}
        <span className="text-muted-foreground text-sm font-medium">
          {selectedRowCount} item{selectedRowCount !== 1 ? 's' : ''} selected
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {renderBulkEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="default" size="sm">
                  <Pencil className="size-4" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    Bulk Edit {selectedRowCount} Item
                    {selectedRowCount !== 1 ? 's' : ''}
                  </DialogTitle>
                </DialogHeader>
                {renderBulkEdit(table, handleClose)}
              </DialogContent>
            </Dialog>
          )}

          <BulkActionMenu
            actions={actions}
            rows={selectedData}
            onRunComplete={() => table.setRowSelection({})}
          />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearSelection}
            aria-label="Clear selection"
          >
            <X className="size-4" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
